import { useMemo, useState } from "react";
import { Brand } from "../components/Brand";
import { Banner } from "../components/ui";
import { ApiError, createApi } from "../lib/api";
import { useAuth } from "../lib/auth";

export function Login() {
  const { signIn, signInWithGoogle, resetPassword, configured, getToken } = useAuth();
  const [mode, setMode] = useState<"signin" | "request">("signin");
  const api = useMemo(() => createApi(getToken), [getToken]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reqName, setReqName] = useState("");
  const [reqCompany, setReqCompany] = useState("");
  const [reqMessage, setReqMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);

  function switchMode(next: "signin" | "request") {
    setMode(next);
    setError(null);
    setNotice(null);
    setSubmitted(false);
  }

  async function onRequestAccess(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    if (!email.trim()) {
      setError("Enter your email to request access.");
      return;
    }
    setBusy(true);
    try {
      const res = await api.submitAccessRequest({
        email: email.trim(),
        name: reqName.trim() || undefined,
        company: reqCompany.trim() || undefined,
        message: reqMessage.trim() || undefined,
      });
      // Already provisioned? Send them to sign in instead of queuing another request.
      if (res.status === "already_provisioned") {
        switchMode("signin");
        setNotice(
          "You already have portal access — just sign in below (use “Continue with Google” if that's how you registered).",
        );
      } else {
        setSubmitted(true);
      }
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not submit your request. Try again later.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      await signIn(email, password);
    } catch (err) {
      const code = (err as { code?: string }).code ?? "";
      if (code === "auth/multi-factor-auth-required") {
        setNotice("Multi-factor authentication required — complete the second factor to continue.");
      } else {
        setError("Sign-in failed. Check your credentials or contact support.");
      }
    } finally {
      setBusy(false);
    }
  }

  async function onGoogle() {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      const code = (err as { code?: string }).code ?? "";
      if (code === "auth/multi-factor-auth-required") {
        setNotice("Multi-factor authentication required — complete the second factor to continue.");
      } else if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
        // User dismissed the popup — no error to surface.
      } else {
        setError("Google sign-in failed. Try again or contact support.");
      }
    } finally {
      setBusy(false);
    }
  }

  async function onReset() {
    setError(null);
    setNotice(null);
    if (!email) {
      setError("Enter your email first, then select “Forgot password.”");
      return;
    }
    try {
      await resetPassword(email);
      setNotice("If that email is provisioned, a set-password link is on its way.");
    } catch {
      setError("Could not send reset email.");
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <Brand />
        </div>
        <div className="card p-8">
          <div className="mb-6">
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-accent">
              Secure access
            </div>
            <h1 className="mt-1 text-xl font-semibold text-ink">
              {mode === "signin" ? "Sign in to the portal" : "Request portal access"}
            </h1>
            <p className="mt-1 text-sm text-ink-muted">
              {mode === "signin"
                ? "Authorized customers only. Access is authenticated, authorized, and audited."
                : "Submit a request and our team will review it. On approval you'll receive a set-password email."}
            </p>
          </div>

          {!configured && (
            <div className="mb-4">
              <Banner tone="error">
                Identity Platform is not configured in this build. Set the VITE_FIREBASE_* env vars.
              </Banner>
            </div>
          )}
          {error && (
            <div className="mb-4">
              <Banner tone="error">{error}</Banner>
            </div>
          )}
          {notice && (
            <div className="mb-4">
              <Banner tone="success">{notice}</Banner>
            </div>
          )}

          {mode === "signin" ? (
            <>
              <form onSubmit={onSubmit} className="space-y-4">
                <div>
                  <label className="label" htmlFor="email">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="username"
                    className="input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="label" htmlFor="password">
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    className="input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
                <button className="btn-primary w-full" type="submit" disabled={busy || !configured}>
                  {busy ? "Signing in…" : "Sign in"}
                </button>
              </form>

              <div className="my-4 flex items-center gap-3">
                <div className="h-px flex-1 bg-white/10" />
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-dim">
                  or
                </span>
                <div className="h-px flex-1 bg-white/10" />
              </div>

              <button
                className="btn-ghost flex w-full items-center justify-center gap-2"
                type="button"
                onClick={onGoogle}
                disabled={busy || !configured}
              >
                <GoogleGlyph />
                Continue with Google
              </button>

              <div className="mt-4 flex items-center justify-between">
                <button
                  className="text-sm text-ink-muted underline-offset-4 hover:text-accent-strong hover:underline"
                  onClick={onReset}
                  type="button"
                >
                  Forgot password?
                </button>
                <button
                  className="text-sm text-ink-muted underline-offset-4 hover:text-accent-strong hover:underline"
                  onClick={() => switchMode("request")}
                  type="button"
                >
                  Request access
                </button>
              </div>
            </>
          ) : submitted ? (
            <div className="space-y-4">
              <Banner tone="success">
                Request received. Our team will review it and, on approval, send a set-password
                email to <span className="font-mono">{email.trim()}</span>.
              </Banner>
              <button
                className="btn-ghost w-full"
                onClick={() => switchMode("signin")}
                type="button"
              >
                Back to sign in
              </button>
            </div>
          ) : (
            <>
              <form onSubmit={onRequestAccess} className="space-y-4">
                <div>
                  <label className="label" htmlFor="req-email">
                    Work email
                  </label>
                  <input
                    id="req-email"
                    type="email"
                    autoComplete="email"
                    className="input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="label" htmlFor="req-name">
                    Full name
                  </label>
                  <input
                    id="req-name"
                    type="text"
                    autoComplete="name"
                    className="input"
                    value={reqName}
                    onChange={(e) => setReqName(e.target.value)}
                  />
                </div>
                <div>
                  <label className="label" htmlFor="req-company">
                    Organization
                  </label>
                  <input
                    id="req-company"
                    type="text"
                    autoComplete="organization"
                    className="input"
                    value={reqCompany}
                    onChange={(e) => setReqCompany(e.target.value)}
                  />
                </div>
                <div>
                  <label className="label" htmlFor="req-message">
                    Reason for access (optional)
                  </label>
                  <textarea
                    id="req-message"
                    className="input min-h-[80px]"
                    value={reqMessage}
                    onChange={(e) => setReqMessage(e.target.value)}
                  />
                </div>
                <button className="btn-primary w-full" type="submit" disabled={busy}>
                  {busy ? "Submitting…" : "Request access"}
                </button>
              </form>

              <div className="my-4 flex items-center gap-3">
                <div className="h-px flex-1 bg-white/10" />
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-dim">
                  or
                </span>
                <div className="h-px flex-1 bg-white/10" />
              </div>

              <button
                className="btn-ghost flex w-full items-center justify-center gap-2"
                type="button"
                onClick={onGoogle}
                disabled={busy || !configured}
              >
                <GoogleGlyph />
                Continue with Google
              </button>
              <p className="mt-2 text-center text-xs text-ink-dim">
                Sign in with Google, then request access if your account isn't provisioned yet.
              </p>

              <button
                className="mt-4 text-sm text-ink-muted underline-offset-4 hover:text-accent-strong hover:underline"
                onClick={() => switchMode("signin")}
                type="button"
              >
                ← Back to sign in
              </button>
            </>
          )}
        </div>
        <p className="mt-6 text-center text-xs text-ink-dim">
          Need help? Contact your PumaMesh representative or{" "}
          <a href="https://support.pumamesh.com" className="text-accent-strong hover:underline">
            support.pumamesh.com
          </a>
          .
        </p>
      </div>
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.02-3.7H.96v2.34A9 9 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.98 10.72a5.4 5.4 0 0 1 0-3.44V4.94H.96a9 9 0 0 0 0 8.12l3.02-2.34z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58C13.46.9 11.42 0 9 0A9 9 0 0 0 .96 4.94l3.02 2.34C4.68 5.16 6.66 3.58 9 3.58z"
      />
    </svg>
  );
}
